// marketHours.js — US equity market session gate (NYSE/NASDAQ)
// Pure, no side effects, no imports. DST handled by Intl (America/New_York).
// Crypto is 24/7 and must NOT be gated by this — callers gate stocks only.
//
// Dated constants need an annual refresh. Verified against the official
// NYSE/ICE 2026 calendar (ir.theice.com / nyse.com/trade/hours-calendars).

// Full-day closures, 2026 (YYYY-MM-DD, America/New_York calendar date)
const NYSE_HOLIDAYS_2026 = [
  "2026-01-01", // New Year's Day
  "2026-01-19", // Martin Luther King Jr. Day
  "2026-02-16", // Washington's Birthday
  "2026-04-03", // Good Friday
  "2026-05-25", // Memorial Day
  "2026-06-19", // Juneteenth
  "2026-07-03", // Independence Day (observed)
  "2026-09-07", // Labor Day
  "2026-11-26", // Thanksgiving Day
  "2026-12-25", // Christmas Day
];

// Early-close days, 2026 — regular open 9:30, close 13:00 ET
const NYSE_EARLY_CLOSES_2026 = [
  "2026-11-27", // Day after Thanksgiving
  "2026-12-24", // Christmas Eve
];

// Return the America/New_York wall-clock parts for a given Date.
// Uses Intl so DST is resolved by the runtime, not by us.
function nyParts(date) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== "literal") parts[p.type] = p.value;
  }
  // hour can come back "24" at midnight in some environments; normalize.
  let hour = parseInt(parts.hour, 10);
  if (hour === 24) hour = 0;
  return {
    weekday: parts.weekday,             // "Mon".."Sun"
    dateStr: `${parts.year}-${parts.month}-${parts.day}`, // "YYYY-MM-DD"
    hour,                               // 0-23 ET
    minute: parseInt(parts.minute, 10), // 0-59 ET
  };
}

// Minutes since midnight ET for easy comparison against session bounds.
function nyMinutes(parts) {
  return parts.hour * 60 + parts.minute;
}

const OPEN_MIN  = 9 * 60 + 30;  // 9:30 ET
const CLOSE_MIN = 16 * 60;      // 16:00 ET
const EARLY_CLOSE_MIN = 13 * 60; // 13:00 ET

// Is the US stock market open at `date` (default now)?
// Weekends closed; full holidays closed; otherwise 9:30-16:00 ET,
// except early-close dates which end at 13:00 ET.
export function isStockMarketOpen(date = new Date()) {
  const p = nyParts(date);

  // Weekend
  if (p.weekday === "Sat" || p.weekday === "Sun") return false;

  // Full-day holiday
  if (NYSE_HOLIDAYS_2026.includes(p.dateStr)) return false;

  const mins = nyMinutes(p);
  if (mins < OPEN_MIN) return false;

  // Early-close days end at 13:00 ET
  if (NYSE_EARLY_CLOSES_2026.includes(p.dateStr)) {
    return mins < EARLY_CLOSE_MIN;
  }

  // Regular session
  return mins < CLOSE_MIN;
}

export { NYSE_HOLIDAYS_2026, NYSE_EARLY_CLOSES_2026 };
